import { MOBILE_SCREEN_QUERY } from "./shared.1cf4cfaf2f6b.js";
import { initFocusTrap } from "./focus-trap.24d9780813f9.js";
const elementTagName = "dialog-backdrop-closer";

/**
 * A <dialog-backdrop-closer> wraps native <dialog> elements to add:
 *
 * - Clicking the backdrop (outside the dialog content) closes the dialog on desktop
 * - Pressing Escape closes the dialog
 * - Focus trap: Tab cycles through focusable elements within the dialog
 * - Optional: add `data-initial-focus` to an element inside the dialog to focus it on open
 */
export class DialogBackdropCloser extends HTMLElement {
  dialog = null;
  observer = null;
  focusTrap = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    if (!this.dialog) {
      console.warn(`No <dialog> found inside <${elementTagName}>`);
      return;
    }

    // Attach click handler for backdrop detection
    this.dialog.addEventListener("click", this.handleDialogClick);

    // Synchronize keyboard handlers with the modal's open attribute
    this.observer = new MutationObserver(this.onDialogStateChange);
    this.observer.observe(this.dialog, {
      attributes: true,
      attributeFilter: ["open"],
    });
    if (this.dialog.hasAttribute("open")) {
      this.attachKeyboardHandlers();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.detachKeyboardHandlers();
  }

  onDialogStateChange = (mutationsList) => {
    for (let mutation of mutationsList) {
      if (mutation.attributeName === "open") {
        if (this.dialog.hasAttribute("open")) {
          this.attachKeyboardHandlers();
          this.focusInitialElement();
        } else {
          this.detachKeyboardHandlers();
        }
      }
    }
  };

  focusInitialElement = () => {
    const initialFocusElement = this.dialog.querySelector(
      "[data-initial-focus]",
    );
    if (initialFocusElement) {
      initialFocusElement.focus();
    }
  };

  handleDialogClick = (event) => {
    if (!MOBILE_SCREEN_QUERY.matches && event.target === this.dialog) {
      this.handleBackdropClick(event);
    } else {
      event.stopPropagation();
    }
  };

  handleBackdropClick = () => {
    this.close();
  };

  attachKeyboardHandlers = () => {
    this.dialog.addEventListener("keydown", this.handleKeydown);
    this.focusTrap = initFocusTrap(this.dialog);
  };

  detachKeyboardHandlers = () => {
    this.dialog.removeEventListener("keydown", this.handleKeydown);
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  };

  handleKeydown = (event) => {
    if (event.key === "Escape") {
      this.close();
    }
  };

  close() {
    this.dialog.close();
  }
}

customElements.define(elementTagName, DialogBackdropCloser);
